import { memo, useCallback, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { useSsrTileImage } from './ssr-tile-image';
import { VideoPreviewPlayer, type onReadyParams } from './VideoPreviewPlayer';
export interface VideoPreviewProps {
  /** Asset title — used as image alt text, which Google reads as the anchor
      text for the tile's link. Never a generic string. */
  alt: string;
  imageUrl: string;
  isPlaying: boolean;
  onReady?: (params?: onReadyParams) => void;
  showVideo: boolean;
  src: string;
}
export const VideoPreview = memo(({
  alt,
  imageUrl,
  isPlaying,
  showVideo,
  src,
  onReady
}: VideoPreviewProps) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Tiles the server paints must not wait for hydration to become visible.
  const {
    isServerPainted,
    isLcpCandidate
  } = useSsrTileImage();
  const handleImageLoaded = useCallback(() => {
    setImageLoaded(true);
  }, []);

  // An image that's already in the browser cache can be complete by the time
  // the element mounts, and `onLoad` may never fire for it. Fading it in
  // would flash pixels the user is already looking at, so treat it as loaded
  // straight away. The ref callback runs at commit, before paint.
  const handleImageRef = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete) {
      setImageLoaded(true);
    }
  }, []);
  const handleOnVideoPreviewReady = useCallback((params?: onReadyParams) => {
    setIsVideoPlaying(true);
    onReady?.(params);
  }, [onReady]);

  // Start hidden (opacity-0) and fade in once the image loads via JS, unless
  // the tile came down in the server HTML — those paint immediately.
  // Hide again when the video player takes over during hover playback.
  const showImage = useMemo(() => !(imageLoaded || isServerPainted) || isPlaying && isVideoPlaying, [imageLoaded, isServerPainted, isPlaying, isVideoPlaying]);
  const className = useMemo(() => cn('pointer-events-none object-cover transition-opacity duration-300', showImage && 'opacity-0'), [showImage]);
  return <div className={cn('absolute inset-0 h-full w-full bg-gray-100 dark:bg-gray-900')}>
        {!!showVideo && <VideoPreviewPlayer isPlaying={isPlaying} onReady={handleOnVideoPreviewReady} setIsVideoPlaying={setIsVideoPlaying} src={src} />}
        <Image alt={alt} className={className} fill loading={isLcpCandidate ? 'eager' : 'lazy'} onLoad={handleImageLoaded}
    // `priority` emits a preload link so the LCP image starts
    // downloading from the HTML, not from hydrated JS.
    priority={isLcpCandidate} ref={handleImageRef} sizes="(min-width: 1600px) 25vw, (min-width: 1280px) 33vw, (min-width: 1024px) 50vw, 100vw" src={imageUrl} />
        {/* Fallback for non-JS clients (crawlers/bots). Client-painted tiles
            start at opacity-0 and fade in on load, but without JS the onLoad
            callback never fires. This <noscript> img is always visible.
            (Server-painted tiles skip the fade-in, so they don't need it.) */}
        <noscript>
          {/* biome-ignore lint/performance/noImgElement: noscript fallback for non-JS clients */}
          {/* No loading="lazy" so non-JS crawlers fetch the tile image. */}
          <img alt={alt} className="absolute inset-0 h-full w-full object-cover" src={imageUrl} />
        </noscript>
      </div>;
});